/**
 * Auto-layout — assigns positions to every node by depth from the start.
 *
 * Pure BFS layered layout, **top-to-bottom**: depth = shortest distance from
 * `start_node` along outgoing edges, depth grows downward. Siblings at the
 * same depth spread horizontally, centered on the canvas axis. This matches
 * the visual language of the cards themselves — input handle on top, output
 * handles on the bottom — so edges always flow downward.
 *
 * Orphaned (unreachable) nodes get parked two rows below the main graph so
 * they're visible but obviously separate.
 *
 * Tradeoff vs. dagre/elkjs: we don't minimize edge crossings or do
 * sophisticated coordinate assignment. For typical flows (5–30 nodes, mostly
 * tree-shaped) this looks clean and avoids a 200KB dependency.
 */

import type { FlowDefinition, FlowNode } from "../../types/flow";
import type { NodePositions } from "./editorStore";

// Horizontal gap between siblings at the same depth.
const COL_GAP = 340;
// Vertical gap between successive depths.
const ROW_GAP = 200;
const ORIGIN_X = 80;
const ORIGIN_Y = 80;

function collectTargets(node: FlowNode): string[] {
  const out: string[] = [];
  switch (node.type) {
    case "trigger":
      if (node.next) out.push(node.next);
      break;
    case "condition":
      if (node.true_next) out.push(node.true_next);
      if (node.false_next) out.push(node.false_next);
      if (node.error_next) out.push(node.error_next);
      break;
    case "switch":
      for (const b of node.branches) if (b.next) out.push(b.next);
      if (node.default_next) out.push(node.default_next);
      if (node.error_next) out.push(node.error_next);
      break;
    case "ab_test":
      for (const v of node.variants) if (v.next) out.push(v.next);
      if (node.error_next) out.push(node.error_next);
      break;
    case "schedule":
      if (node.active_next) out.push(node.active_next);
      if (node.inactive_next) out.push(node.inactive_next);
      if (node.error_next) out.push(node.error_next);
      break;
    case "checkout_page":
      if (node.next) out.push(node.next);
      break;
    case "capture_policy":
      break;
    case "delay":
      if (node.next) out.push(node.next);
      if (node.error_next) out.push(node.error_next);
      break;
    case "fraud_check":
      if (node.low_next) out.push(node.low_next);
      if (node.medium_next) out.push(node.medium_next);
      if (node.high_next) out.push(node.high_next);
      if (node.error_next) out.push(node.error_next);
      break;
    case "http_request":
      if (node.next) out.push(node.next);
      if (node.error_next) out.push(node.error_next);
      break;
  }
  return out;
}

export function arrangeLayout(definition: FlowDefinition): NodePositions {
  // BFS from start_node — first time we see a node sets its depth.
  const depth: Record<string, number> = {};
  depth[definition.start_node] = 0;
  const queue: string[] = [definition.start_node];

  while (queue.length > 0) {
    const id = queue.shift()!;
    const node = definition.nodes[id];
    if (!node) continue;
    for (const t of collectTargets(node)) {
      if (depth[t] === undefined && definition.nodes[t]) {
        depth[t] = depth[id] + 1;
        queue.push(t);
      }
    }
  }

  // Group ids by depth, preserving insertion order (which is BFS order from
  // the trigger — gives a stable, predictable left-to-right layout).
  const byDepth = new Map<number, string[]>();
  for (const id of Object.keys(definition.nodes)) {
    const d = depth[id];
    if (d === undefined) continue;
    if (!byDepth.has(d)) byDepth.set(d, []);
    byDepth.get(d)!.push(id);
  }

  const maxDepth = Math.max(0, ...byDepth.keys());

  // Park orphans (no path from the trigger) two columns to the right of the
  // main graph so they're visible but obviously separate.
  const orphans = Object.keys(definition.nodes).filter(
    (id) => depth[id] === undefined,
  );
  if (orphans.length > 0) {
    byDepth.set(maxDepth + 2, orphans);
  }

  const positions: NodePositions = {};
  // Center each depth-row horizontally around the canvas axis defined by the
  // widest row, so the graph reads as a centered tree growing downward.
  const widest = Math.max(
    1,
    ...Array.from(byDepth.values(), (arr) => arr.length),
  );
  for (const [d, ids] of byDepth) {
    const rowCenterX = ORIGIN_X + ((widest - 1) * COL_GAP) / 2;
    const myCenterX = rowCenterX - ((ids.length - 1) * COL_GAP) / 2;
    ids.forEach((id, i) => {
      positions[id] = {
        x: myCenterX + i * COL_GAP,
        y: ORIGIN_Y + d * ROW_GAP,
      };
    });
  }

  return positions;
}
